/**
 * features/finance/partyJournalWrite.ts
 * ------------------------------------------------------------------
 * Writing debtor and creditor journals.
 *
 * TWO WRITES, AND THE LEDGER ONE COMES FIRST. The GL journal is posted
 * before the party-journal record exists, because a party journal whose
 * ledger entry failed is a correction the account shows and the books do
 * not — the control account stops tying and nothing says why. The other
 * order fails safely: a posted journal with no party record is visible in
 * the ledger and can be traced.
 *
 * NEVER EDITED. A wrong journal is reversed by an opposite one and both
 * stay on the account, the same rule the ledger and the petty-cash slips
 * follow. Editing a correction is how a correction becomes untraceable.
 *
 * IT WILL NOT MOVE MONEY. The contra is refused if it is a bank or cash
 * account: that would be a payment recorded without receipting or
 * reconciliation, which is what the cash book and the payment screens are
 * for. Refused rather than warned about, because a warning on the one
 * path that bypasses two controls is not a control.
 * ------------------------------------------------------------------ */
import { collection, doc, runTransaction } from 'firebase/firestore';
import { db, setDoc, updateDoc } from '@/lib/firebase';
import {
  PARTY_JOURNAL_COLLECTION, partyCounterId, formatPartyRef,
  partyJournalLines, contraIsCashLike,
  type PartyJournal, type PartySide, type PartyDirection, type PartyAccounts,
} from '@/lib/partyJournals';
import { postJournal } from '@/features/finance/glWrite';

const COUNTERS = 'financeCounters';
const now = () => new Date().toISOString();

export interface NewPartyJournalInput {
  side: PartySide;
  direction: PartyDirection;
  partyId: string;
  partyName: string;
  contraAccountCode: string;
  amount: number;
  reason: string;
  date: string;
  accounts: PartyAccounts;
  /** Resolves an account's system key, so a bank contra can be refused. */
  systemKeyOf: (code: string) => string | undefined;
  actor: { uid?: string; name?: string; email?: string };
}

export async function postPartyJournal(input: NewPartyJournalInput): Promise<PartyJournal> {
  if (!input.partyId) throw new Error('Choose whose account this corrects.');
  if (!input.reason.trim()) throw new Error('Say why the account is being corrected.');

  if (contraIsCashLike(input.contraAccountCode, input.systemKeyOf)) {
    throw new Error(
      'That is a bank or cash account. A journal here cannot move money — record a receipt '
      + 'or a payment instead, so it is numbered and reconciled.',
    );
  }

  const year = input.date.slice(0, 4);
  const at = now();

  /* The reference is taken first, so the narration on the ledger lines can
     carry it. A burnt number is a gap in a series; a journal that cannot
     be traced back to its party record is worse. */
  const ref = await runTransaction(db, async (tx) => {
    const counterRef = doc(db, COUNTERS, partyCounterId(year));
    const snap = await tx.get(counterRef);
    const seq = ((snap.exists() ? Number(snap.data().seq) : 0) || 0) + 1;
    tx.set(counterRef, { seq, updatedAt: at }, { merge: true });
    return formatPartyRef(input.side, seq, year);
  });

  const lines = partyJournalLines({
    side: input.side,
    direction: input.direction,
    amount: input.amount,
    partyName: input.partyName,
    reason: input.reason,
    journalRef: ref,
  }, input.accounts);

  const id = doc(collection(db, PARTY_JOURNAL_COLLECTION)).id;

  const posted = await postJournal({
    type: 'party_journal',
    sourceId: id,
    date: input.date,
    narration: `${ref} ${input.partyName} — ${input.reason}`,
    lines,
    actor: input.actor,
  });

  const record: PartyJournal = {
    id,
    journalRef: ref,
    side: input.side,
    direction: input.direction,
    partyId: input.partyId,
    partyName: input.partyName,
    contraAccountCode: input.contraAccountCode,
    amount: Math.round(Number(input.amount) * 100) / 100,
    reason: input.reason.trim(),
    date: input.date,
    fy: `FY${year}`,
    journalId: posted.id,
    createdBy: input.actor.uid ?? '',
    ...(input.actor.name ? { createdByName: input.actor.name } : {}),
    createdAt: at,
  };
  await setDoc(doc(db, PARTY_JOURNAL_COLLECTION, id), record);
  return record;
}

/**
 * Reverse one by posting its opposite.
 *
 * The reversal is dated TODAY, not on the original's date: it is a
 * decision made now, and back-dating it would rewrite a month that may
 * already be closed — the same rule the posting engine follows for a
 * voided invoice.
 */
export async function reversePartyJournal(
  original: PartyJournal,
  accounts: PartyAccounts,
  systemKeyOf: (code: string) => string | undefined,
  actor: { uid?: string; name?: string; email?: string },
): Promise<PartyJournal> {
  if (original.reversedBy) throw new Error('That journal has already been reversed.');
  if (original.reversesId) throw new Error('That journal is itself a reversal.');

  const opposite: PartyDirection = original.direction === 'charge' ? 'credit' : 'charge';

  const reversal = await postPartyJournal({
    side: original.side,
    direction: opposite,
    partyId: original.partyId,
    partyName: original.partyName,
    contraAccountCode: original.contraAccountCode,
    amount: original.amount,
    reason: `Reverses ${original.journalRef}: ${original.reason}`,
    date: now().slice(0, 10),
    accounts,
    systemKeyOf,
    actor,
  });

  await updateDoc(doc(db, PARTY_JOURNAL_COLLECTION, original.id), {
    reversedBy: reversal.id,
  });
  await updateDoc(doc(db, PARTY_JOURNAL_COLLECTION, reversal.id), {
    reversesId: original.id,
  });

  return reversal;
}
